import React from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { yupResolver } from '@hookform/resolvers'
import { ControlledSelect, ControlledTextInput } from 'transfix-form/components'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Box, Link, makeStyles, Typography } from 'transfix-ui/core'
import { IShipperRate } from 'apps/rms/types/shipperRates'
import { updateShipperRate } from 'apps/rms/utils/actions/shipperRates'
import { AcceptanceCapSchema, IAcceptanceCapSchema } from './AcceptanceCapSchema'

interface IUpdateAcceptanceCapModalProps extends Omit<IModalBaseProps, 'modalType'> {
  onClose: () => void
  shipperRate?: IShipperRate
}

export const acceptanceCapCadences = [
  {
    value: 'daily',
    label: 'Daily',
  },
  {
    value: 'weekly',
    label: 'Weekly',
  },
  {
    value: 'monthly',
    label: 'Monthly',
  },
  {
    value: 'yearly',
    label: 'Yearly',
  },
]

const useUpdateAcceptanceCapModalStyles = makeStyles({
  body: {},
  modal: {},
  header: {},
  title: {},
  rateId: {
    marginBottom: '1em',
  },
  root: {},
})

export const UpdateAcceptanceCapModal = ({
  onClose,
  shipperRate,
  ...props
}: IUpdateAcceptanceCapModalProps) => {
  const methods = useForm({
    resolver: yupResolver(AcceptanceCapSchema),
    defaultValues: {
      acceptanceCap: shipperRate?.acceptanceCap,
      acceptanceCapCadence: shipperRate?.acceptanceCapCadence,
    },
  })

  const dispatch = useDispatch()

  const { control, errors, handleSubmit } = methods

  const classes = useUpdateAcceptanceCapModalStyles()

  const onSubmit = async (formParams: IAcceptanceCapSchema) => {
    if (shipperRate) {
      shipperRate.acceptanceCap = formParams.acceptanceCap
      shipperRate.acceptanceCapCadence = formParams.acceptanceCapCadence
      dispatch(updateShipperRate(shipperRate))
      onClose()
    }
  }

  const submitHandler = handleSubmit(onSubmit)

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  return (
    <ModalBase
      onClose={onClose}
      modalType={MODAL_TYPES.form}
      title='Lane Acceptance Cap'
      size={2}
      open
      actions={actions}
      className={classes.root}
      bodyClassName={classes.body}
      titleClass={classes.title}
      modalClassName={classes.modal}
      headerClassName={classes.header}
      closeOnBackdropClick
      {...props}>
      <Typography variant='body2'>
        <Link
          href='https://transfix.atlassian.net/wiki/spaces/SUG/pages/2536079361/Rate+Management+System+RMS'
          target='_blank'
          rel='noopener noreferrer'>
          What is a lane acceptance cap?
        </Link>
      </Typography>
      <Typography className={classes.rateId}>Award ID: {shipperRate?.id}</Typography>

      <Box mb={2}>
        <ControlledSelect
          control={control}
          defaultValue={shipperRate?.acceptanceCapCadence || acceptanceCapCadences[0].value}
          name='acceptanceCapCadence'
          SelectProps={{
            label: 'Acceptance Cap Cadence',
            options: acceptanceCapCadences,
            dataTest: 'acceptance-cap-cadence',
            large: true,
            required: true,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledTextInput
          control={control}
          name='acceptanceCap'
          defaultValue={shipperRate?.acceptanceCap}
          TextInputProps={{
            label: 'Acceptance Cap',
            placeholder: 'Acceptance Cap',
            required: true,
            validationMessage: errors.acceptanceCap?.message,
          }}
        />
      </Box>
    </ModalBase>
  )
}
